import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search } from "lucide-react";
import { buildAdminHomeIndex, getGroupStyle, searchAdminHome } from "../adminHomeConfig";

interface AdminCommandPaletteProps {
  open: boolean;
  onClose: () => void;
}

/**
 * Cmd/Ctrl+K command palette for the admin home.
 * Destination search only (v1) -- searches the admin tile index, never records.
 */
const AdminCommandPalette: React.FC<AdminCommandPaletteProps> = ({ open, onClose }) => {
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);
  const [query, setQuery] = useState("");
  const [activeIndex, setActiveIndex] = useState(0);
  const index = useMemo(() => buildAdminHomeIndex(), []);
  const results = useMemo(() => searchAdminHome(index, query), [index, query]);

  // Reset + focus whenever the palette opens.
  useEffect(() => {
    if (!open) return;
    setQuery("");
    setActiveIndex(0);
    const id = window.setTimeout(() => inputRef.current?.focus(), 0);
    return () => window.clearTimeout(id);
  }, [open]);

  // Keep the highlight in range as results change.
  useEffect(() => {
    setActiveIndex(prev => (results.length === 0 ? 0 : Math.min(prev, results.length - 1)));
  }, [results.length]);

  if (!open) return null;

  const go = (path: string): void => {
    onClose();
    navigate(path);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>): void => {
    if (event.key === "Escape") {
      event.preventDefault();
      onClose();
    } else if (event.key === "ArrowDown") {
      event.preventDefault();
      setActiveIndex(prev => (results.length === 0 ? 0 : Math.min(prev + 1, results.length - 1)));
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      setActiveIndex(prev => Math.max(prev - 1, 0));
    } else if (event.key === "Enter") {
      event.preventDefault();
      const selected = results[activeIndex];
      if (selected) go(selected.path);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center bg-black/60 px-4 pt-[14vh] backdrop-blur-sm"
      onMouseDown={event => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div
        role="dialog"
        aria-label="Admin command palette"
        onKeyDown={handleKeyDown}
        className="w-full max-w-xl overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-2xl dark:border-rp-separator dark:bg-rp-surface-raised"
      >
        <div className="flex items-center gap-3 border-b border-gray-200 px-4 py-3 dark:border-rp-separator">
          <Search className="h-5 w-5 shrink-0 text-rp-amber" />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={event => setQuery(event.target.value)}
            placeholder="Search admin tools..."
            autoComplete="off"
            spellCheck={false}
            className="flex-1 bg-transparent text-base text-gray-900 placeholder-gray-400 outline-none dark:text-white dark:placeholder-gray-500"
          />
        </div>

        <div className="max-h-[50vh] overflow-y-auto p-2">
          {results.length === 0 ? (
            <div className="px-3 py-6 text-center text-sm text-gray-500 dark:text-gray-400">
              No destinations match. This finds admin tools, not records.
            </div>
          ) : (
            results.map((item, i) => {
              const style = getGroupStyle(item.group ?? "");
              const Icon = item.icon;
              const isActive = i === activeIndex;
              return (
                <button
                  key={item.id}
                  type="button"
                  onMouseEnter={() => setActiveIndex(i)}
                  onClick={() => go(item.path)}
                  className={`flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left transition-colors ${
                    isActive ? "bg-gray-100 dark:bg-rp-surface" : ""
                  }`}
                >
                  <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${style.chip}`}>
                    <Icon className="h-4 w-4" />
                  </span>
                  <span className="text-sm font-semibold text-gray-900 dark:text-white">{item.label}</span>
                  <span className="ml-auto font-mono text-[10px] uppercase tracking-wider text-gray-400 dark:text-gray-500">
                    {item.group}
                  </span>
                </button>
              );
            })
          )}
        </div>

        <div className="flex gap-4 border-t border-gray-200 px-4 py-2 font-mono text-[11px] text-gray-400 dark:border-rp-separator dark:text-gray-500">
          <span>up / down navigate</span>
          <span>enter open</span>
          <span>esc close</span>
        </div>
      </div>
    </div>
  );
};

export default AdminCommandPalette;
